import { cn } from '@noor/lib/cn';

export interface PillTabItem<T extends string = string> {
  value: T;
  label: string;
  badge?: number | string;
}

export interface PillTabsProps<T extends string = string> {
  items: readonly PillTabItem<T>[];
  value: T;
  onChange: (val: T) => void;
  className?: string;
  itemClassName?: string;
}

/**
 * Button-based tab bar matching the Radix `Tabs` pill treatment (Figma node
 * 762:1676): a white `rounded-full` track holding borderless 16px-medium
 * soft-ink pills; a hovered pill gains the hairline border + near-black text;
 * the active pill fills solid brand orange with 16px-bold white text.
 *
 * Use this over `Tabs` when the tabs drive external state (URL params, query
 * filters) rather than showing/hiding local panels — each item optionally
 * carries a count badge.
 */
export function PillTabs<T extends string = string>({
  items,
  value,
  onChange,
  className,
  itemClassName,
}: PillTabsProps<T>) {
  return (
    <div
      role="tablist"
      className={cn(
        'flex w-fit max-w-full items-center gap-2 overflow-x-auto rounded-full bg-tabs-track-bg p-2.5 [-ms-overflow-style:none] [scrollbar-width:none] sm:flex-wrap [&::-webkit-scrollbar]:hidden',
        className,
      )}
    >
      {items.map((tab) => {
        const isActive = tab.value === value;
        return (
          <button
            key={tab.value}
            role="tab"
            type="button"
            aria-selected={isActive}
            onClick={() => onChange(tab.value)}
            className={cn(
              // Pill type resolves through `--tabs-pill-*` sizing handles, shared
              // with the Radix `Tabs` pill variant (same Figma node).
              'inline-flex shrink-0 cursor-pointer items-center justify-center gap-1 rounded-lg border border-transparent px-2.5 py-2 text-[length:var(--tabs-pill-font-size)] leading-6 font-[var(--tabs-pill-font-weight)] tracking-[-0.32px] whitespace-nowrap text-tabs-pill-fg transition-colors outline-none hover:rounded-full hover:border-tabs-pill-border-hover hover:text-tabs-pill-fg-hover focus-visible:ring-2 focus-visible:ring-ring/40',
              isActive &&
                'rounded-full border-transparent bg-primary font-[var(--tabs-pill-font-weight-active)] tracking-[-0.48px] text-primary-foreground hover:border-transparent hover:text-primary-foreground',
              itemClassName,
            )}
          >
            <span>{tab.label}</span>
            {tab.badge !== undefined && (
              <span
                className={cn(
                  'rounded-full px-1.5 py-0.5 text-[11px] leading-none font-bold',
                  isActive ? 'bg-white/20 text-white' : 'bg-muted text-muted-foreground',
                )}
              >
                {tab.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
